import {
  Box,
  Button,
  CountryFlag,
  FlexBox,
  IconButton,
  Switch,
  Table,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { Satisfaction } from 'lib-shared-legacy-types';
import { getCountryCodeFromPhoneNumber } from 'lib-shared-phone-numbers';
import { useCallback, useMemo } from 'react';

import { ratingApi } from 'apis/rating';
import { appActions } from 'app/app.actions';
import { getCountryNameFromCountryCode } from 'utils/countries';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import styles from './phoneSurveysTableRow.module.scss';
import { PhoneSurveysTableRowProps } from './phoneSurveysTableRow.types';

/** Represents a row of the link between a phone number and a survey. */
export const PhoneSurveyTableRow = ({
  phoneSurveyLink,
  onItemChanged,
  isSenderNameVisible,
}: PhoneSurveysTableRowProps): JSX.Element => {
  const [unlinkRatingSettings] = ratingApi.useLazyUnlinkRatingSettingsQuery();

  const { countryCode, countryName } = useMemo(() => {
    const countryCode = getCountryCodeFromPhoneNumber(
      phoneSurveyLink.phoneNumber.e164_format,
    );

    return {
      countryCode,
      countryName: countryCode && getCountryNameFromCountryCode(countryCode),
    };
  }, [phoneSurveyLink.phoneNumber]);

  const dispatch = useAppDispatch();

  const openSelectSurveyModal = () => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:selectSurvey',
          arg: {
            contactPoint: e164_format,
            contactPointKind: 'phone_number',
            onSurveyLinked: (newLink) =>
              onItemChanged({
                phoneNumber: phoneSurveyLink.phoneNumber,
                link: newLink,
              }),
            currentSurvey: link,
          },
        },
      }),
    );
  };

  const {
    phoneNumber: { e164_format, name },
    link,
  } = phoneSurveyLink;

  const handleUnlink = useCallback(() => {
    link &&
      unlinkRatingSettings({
        rating_settings_id: link.rating_settings_id,
        contact_point: link?.contact_point,
        contact_point_kind: link?.contact_point_kind,
      }).then(() =>
        // removes the survey link from the row
        onItemChanged({
          phoneNumber: phoneSurveyLink.phoneNumber,
          link: undefined,
        }),
      );
  }, [link, onItemChanged, phoneSurveyLink.phoneNumber, unlinkRatingSettings]);

  return (
    <>
      <Table.Tr>
        <Table.Td>
          <FlexBox gap={'300'}>
            {countryCode && (
              <Box className={styles.countryFlagContainer}>
                <CountryFlag countryCode={countryCode} size={20} />
              </Box>
            )}
            <FlexBox flexDirection="column">
              <Text.Paragraph variant="2">{e164_format}</Text.Paragraph>
              {countryName && (
                <Text.Paragraph variant="4" subdued>
                  {countryName}
                </Text.Paragraph>
              )}
            </FlexBox>
          </FlexBox>
        </Table.Td>

        <Table.Td>{name}</Table.Td>

        <Table.Td>
          {link ? (
            <Text.Paragraph>{link.name}</Text.Paragraph>
          ) : (
            <Button
              icon="plus"
              variant="secondary"
              disabled={false}
              onClick={openSelectSurveyModal}
            >
              Connect a survey
            </Button>
          )}
        </Table.Td>

        <Table.Td>
          {link && link?.survey_type && (
            <Text.Paragraph>
              {Satisfaction.prettySurveyType(link?.survey_type)}
            </Text.Paragraph>
          )}
        </Table.Td>

        {isSenderNameVisible && (
          <Table.Td>
            {link?.additional_settings?.sender_name &&
              link?.survey_type == 'link' && (
                <Text.Paragraph>
                  {link.additional_settings?.sender_name}
                </Text.Paragraph>
              )}
          </Table.Td>
        )}

        <Table.Td align="center">
          <Switch
            className={styles.switch}
            name={name ?? ''}
            value={name ?? ''}
            checked={!!link}
            disabled={!link}
            onChange={handleUnlink}
          />
        </Table.Td>

        <Table.Td>
          {link && (
            <Tooltip content="Edit" dark>
              <IconButton
                variant="tertiary"
                icon="pen"
                onClick={openSelectSurveyModal}
              />
            </Tooltip>
          )}
        </Table.Td>
      </Table.Tr>
    </>
  );
};
